import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Company } from "../../generated/types";
import { VALIDATION_RULES, validateForm } from "../../utils/formValidation";
import { Button } from "../common/Button";
import { CityField } from "../common/CityField";
import { FormInput, FormLayout } from "../common/FormLayout";

export const RegisterCompanyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { formData: accountStorage } = location.state || {};

  const [formData, setFormData] = useState<Company>({
    id: -1,
    company_name: "",
    siret: "",
    address: "",
    address_complementary: "",
    zip_code: "",
    city: "",
    country: "FR",
    phone_mobile: "",
    phone_fixe: "",
    email: "",
    id_x3: 0,
    creation_date: new Date().toISOString(),
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const getValidationRules = () => ({
    company_name: [
      VALIDATION_RULES.required("La raison sociale est requise"),
      VALIDATION_RULES.minLength(2),
    ],
    siret: [VALIDATION_RULES.required(), VALIDATION_RULES.siret()],
    address: [
      VALIDATION_RULES.required("L'adresse est requise"),
      VALIDATION_RULES.minLength(5),
    ],
    zip_code: [VALIDATION_RULES.required(), VALIDATION_RULES.postalCode()],
    city: [VALIDATION_RULES.required("La ville est requise")],
    phone_mobile: [VALIDATION_RULES.required(), VALIDATION_RULES.phone()],
    phone_fixe: [VALIDATION_RULES.required(), VALIDATION_RULES.phone()],
    email: [VALIDATION_RULES.required(), VALIDATION_RULES.email()],
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationRules = getValidationRules();
    const validationErrors = validateForm(formData, validationRules);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    navigate("/register/farm-type", {
      state: {
        formData: {
          ...accountStorage,
          company: formData,
        },
      },
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "zip_code" && { city: "" }),
    }));

    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCityChange = (city: string, zipCode: string) => {
    setFormData({ ...formData, city, zip_code: zipCode });
    if (errors.city) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.city;
        return newErrors;
      });
    }
  };

  return (
    <FormLayout title="Inscription - Informations de l'entreprise">
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormInput
          label="Raison sociale"
          type="text"
          name="company_name"
          value={formData.company_name}
          onChange={handleChange}
          required
          error={errors.company_name}
        />

        <FormInput
          label="SIRET"
          type="text"
          name="siret"
          value={formData.siret}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "").slice(0, 14);
            setFormData({ ...formData, siret: value });
            if (errors.siret) {
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.siret;
                return newErrors;
              });
            }
          }}
          required
          maxLength={14}
          error={errors.siret}
        />

        <FormInput
          label="Adresse"
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          required
          error={errors.address}
        />

        <FormInput
          label="Complément d'adresse"
          name="address_complementary"
          value={formData.address_complementary}
          onChange={handleChange}
        />

        <div className="flex gap-2">
          <FormInput
            label="Code postal"
            name="zip_code"
            value={formData.zip_code}
            onChange={handleChange}
            required
            maxLength={5}
            error={errors.zip_code}
          />
          <div className="flex-1">
            <CityField
              zipCode={formData.zip_code}
              value={formData.city}
              onChange={handleCityChange}
              required
              error={errors.city}
            />
          </div>
        </div>

        <FormInput
          label="Pays"
          name="id_country"
          value="France"
          onChange={handleChange}
          required
          disabled
        />

        <FormInput
          label="Numéro de téléphone mobile"
          type="tel"
          name="phone_mobile"
          value={formData.phone_mobile}
          onChange={handleChange}
          required
          error={errors.phone_mobile}
        />

        <FormInput
          label="Numéro de téléphone fixe"
          type="tel"
          name="phone_fixe"
          value={formData.phone_fixe}
          onChange={handleChange}
          error={errors.phone_fixe}
        />

        <FormInput
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          error={errors.email}
        />

        <div className="flex gap-4">
          <Button
            type="button"
            variant="secondary"
            size="full"
            onClick={() => navigate(-1)}
          >
            Retour
          </Button>
          <Button type="submit" size="full">
            SUIVANT
          </Button>
        </div>
      </form>
    </FormLayout>
  );
};
